import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LocalSchema = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [faqs, setFaqs] = useState({});
  const [services, setServices] = useState([]);
  const [localBusiness, setLocalBusiness] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState('dental-implants');
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
  const [newEvent, setNewEvent] = useState({ name: '', description: '', start_date: '', end_date: '' });
  const [copied, setCopied] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [faqRes, servicesRes, localRes, eventsRes] = await Promise.all([
        fetch(`${API_URL}/api/schema/faqs/all`),
        fetch(`${API_URL}/api/schema/services/all`),
        fetch(`${API_URL}/api/schema/local-business`),
        fetch(`${API_URL}/api/schema/events`)
      ]);
      if (faqRes.ok) setFaqs((await faqRes.json()).faqs_by_service || {});
      if (servicesRes.ok) setServices((await servicesRes.json()).services || []);
      if (localRes.ok) setLocalBusiness(await localRes.json());
      if (eventsRes.ok) setEvents((await eventsRes.json()).events || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addFaq = async () => {
    if (!newFaq.question || !newFaq.answer) return;
    await fetch(`${API_URL}/api/schema/faq/${selectedService}?question=${encodeURIComponent(newFaq.question)}&answer=${encodeURIComponent(newFaq.answer)}`, { method: 'POST' });
    setNewFaq({ question: '', answer: '' });
    fetchData();
  };

  const addEvent = async () => {
    if (!newEvent.name || !newEvent.description || !newEvent.start_date) return;
    await fetch(`${API_URL}/api/schema/event?name=${encodeURIComponent(newEvent.name)}&description=${encodeURIComponent(newEvent.description)}&start_date=${newEvent.start_date}&end_date=${newEvent.end_date || newEvent.start_date}`, { method: 'POST' });
    setNewEvent({ name: '', description: '', start_date: '', end_date: '' });
    fetchData();
  };

  const copySchema = (schema, label) => {
    navigator.clipboard.writeText(JSON.stringify(schema, null, 2));
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'faq', label: 'FAQ Schema', icon: '❓' },
    { id: 'services', label: 'Service Schema', icon: '🦷' },
    { id: 'local', label: 'Local Business', icon: '📍' },
    { id: 'events', label: 'Events', icon: '📅' },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet><title>Local Schema | Admin</title></Helmet>
      
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6">
        <h1 className="text-2xl font-bold">🔧 Local Schema Enhancement</h1>
        <p className="text-emerald-100">Manage structured data for rich search results</p>
      </div>

      <div className="p-6">
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap ${activeTab === tab.id ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <h3 className="font-semibold text-teal-800">📈 Schema Markup Benefits</h3>
              <p className="text-teal-700 text-sm mt-1">Structured data helps search engines understand your content better, enabling rich results like FAQ dropdowns, star ratings, and event listings in search results.</p>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <p className="text-3xl font-bold text-emerald-600">{Object.values(faqs).flat().length}</p>
                <p className="text-gray-500 text-sm">Total FAQs</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <p className="text-3xl font-bold text-blue-600">{services.length}</p>
                <p className="text-gray-500 text-sm">Services</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <p className="text-3xl font-bold text-purple-600">{events.length}</p>
                <p className="text-gray-500 text-sm">Events</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center">
                <p className="text-3xl font-bold text-orange-600">4</p>
                <p className="text-gray-500 text-sm">Schema Types</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b"><h3 className="font-semibold">Schema Types Implemented</h3></div>
              <div className="divide-y">
                <div className="p-4 flex justify-between items-center">
                  <div><span className="text-xl mr-2">❓</span> <span className="font-medium">FAQPage Schema</span></div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-sm rounded">✓ Active</span>
                </div>
                <div className="p-4 flex justify-between items-center">
                  <div><span className="text-xl mr-2">⭐</span> <span className="font-medium">AggregateRating Schema</span></div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-sm rounded">✓ Active</span>
                </div>
                <div className="p-4 flex justify-between items-center">
                  <div><span className="text-xl mr-2">🏥</span> <span className="font-medium">LocalBusiness (Dentist) Schema</span></div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-sm rounded">✓ Active</span>
                </div>
                <div className="p-4 flex justify-between items-center">
                  <div><span className="text-xl mr-2">🦷</span> <span className="font-medium">Service/MedicalProcedure Schema</span></div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-sm rounded">✓ Active</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAQ SCHEMA */}
        {activeTab === 'faq' && (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-800">❓ FAQ Schema Markup</h3>
              <p className="text-yellow-700 text-sm mt-1">FAQ schema enables FAQ-style rich results in Google Search, showing your questions and answers directly in search results.</p>
            </div>

            <div className="flex gap-2 flex-wrap">
              {Object.keys(faqs).map(service => (
                <button
                  key={service}
                  onClick={() => setSelectedService(service)}
                  className={`px-3 py-1 rounded text-sm ${selectedService === service ? 'bg-teal-600 text-white' : 'bg-gray-200'}`}
                >
                  {service} ({faqs[service]?.length || 0})
                </button>
              ))}
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold">FAQs for: {selectedService}</h3>
                <button
                  onClick={() => copySchema({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: (faqs[selectedService] || []).map(f => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })) }, selectedService)}
                  className="text-sm bg-gray-100 px-3 py-1 rounded hover:bg-gray-200"
                >
                  {copied === selectedService ? '✓ Copied!' : '📋 Copy Schema'}
                </button>
              </div>
              <div className="divide-y">
                {(faqs[selectedService] || []).map((faq, i) => (
                  <div key={i} className="p-4">
                    <p className="font-medium text-teal-700">Q: {faq.question}</p>
                    <p className="text-gray-600 text-sm mt-1">A: {faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <h4 className="font-semibold mb-3">Add Custom FAQ</h4>
              <div className="space-y-3">
                <input placeholder="Question" value={newFaq.question} onChange={e => setNewFaq({...newFaq, question: e.target.value})} className="w-full border rounded px-3 py-2" />
                <textarea placeholder="Answer" value={newFaq.answer} onChange={e => setNewFaq({...newFaq, answer: e.target.value})} className="w-full border rounded px-3 py-2" rows={3} />
                <button onClick={addFaq} className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700">Add FAQ</button>
              </div>
            </div>
          </div>
        )}

        {/* SERVICES SCHEMA */}
        {activeTab === 'services' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800">🦷 Service & Medical Procedure Schema</h3>
              <p className="text-blue-700 text-sm mt-1">Service schema helps search engines understand what services you offer, their prices, and who provides them.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {services.map(service => (
                <div key={service.slug} className="bg-white rounded-lg shadow">
                  <div className="p-4 border-b flex justify-between items-center">
                    <h4 className="font-semibold">{service.name}</h4>
                    <button
                      onClick={() => copySchema(service.schema, service.slug)}
                      className="text-xs bg-gray-100 px-2 py-1 rounded hover:bg-gray-200"
                    >
                      {copied === service.slug ? '✓ Copied!' : '📋 Copy'}
                    </button>
                  </div>
                  <div className="p-4 text-sm space-y-2">
                    <p className="text-gray-600">{service.description}</p>
                    <p><span className="font-medium">Price:</span> {service.price_range}</p>
                    <p><span className="font-medium">Duration:</span> {service.duration}</p>
                    {service.provider && <p><span className="font-medium">Provider:</span> {service.provider}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* LOCAL BUSINESS SCHEMA */}
        {activeTab === 'local' && localBusiness && (
          <div className="space-y-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="font-semibold text-purple-800">📍 LocalBusiness (Dentist) Schema</h3>
              <p className="text-purple-700 text-sm mt-1">Comprehensive schema including business info, address, hours, ratings, and services. This is the foundation of your local SEO structured data.</p>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold">Fort Worth Dental - LocalBusiness Schema</h3>
                <button
                  onClick={() => copySchema(localBusiness.schema, 'local')}
                  className="bg-purple-100 text-purple-700 px-3 py-1 rounded hover:bg-purple-200"
                >
                  {copied === 'local' ? '✓ Copied!' : '📋 Copy Full Schema'}
                </button>
              </div>
              <div className="p-4">
                <pre className="bg-gray-50 p-4 rounded text-xs overflow-x-auto max-h-96">
                  {JSON.stringify(localBusiness.schema, null, 2)}
                </pre>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg shadow">
                <h4 className="font-semibold text-gray-700 mb-2">Business Info</h4>
                <p className="text-sm">Fort Worth Dental</p>
                <p className="text-sm text-gray-500">(817) 985-3337</p>
                <p className="text-sm text-gray-500">4620 Citylake Blvd W</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h4 className="font-semibold text-gray-700 mb-2">Rating</h4>
                <p className="text-2xl font-bold text-yellow-500">★ 4.9</p>
                <p className="text-sm text-gray-500">1,100+ reviews</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h4 className="font-semibold text-gray-700 mb-2">Hours</h4>
                <p className="text-sm">Mon-Thu: 8am-5pm</p>
                <p className="text-sm">Fri: 8am-2pm</p>
              </div>
            </div>
          </div>
        )}

        {/* EVENTS */}
        {activeTab === 'events' && (
          <div className="space-y-6">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="font-semibold text-orange-800">📅 Event Schema</h3>
              <p className="text-orange-700 text-sm mt-1">Event schema can highlight special promotions, community events, or open house days in search results with date and time information.</p>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <h4 className="font-semibold mb-3">Create New Event</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <input placeholder="Event Name" value={newEvent.name} onChange={e => setNewEvent({...newEvent, name: e.target.value})} className="border rounded px-3 py-2" />
                <input type="date" value={newEvent.start_date} onChange={e => setNewEvent({...newEvent, start_date: e.target.value})} className="border rounded px-3 py-2" />
                <textarea placeholder="Description" value={newEvent.description} onChange={e => setNewEvent({...newEvent, description: e.target.value})} className="border rounded px-3 py-2 md:col-span-2" rows={2} />
                <button onClick={addEvent} className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700">Create Event</button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b"><h3 className="font-semibold">Events ({events.length})</h3></div>
              <div className="divide-y">
                {events.map(event => (
                  <div key={event.id} className="p-4 flex justify-between items-center">
                    <div>
                      <p className="font-medium">{event.name}</p>
                      <p className="text-sm text-gray-500">{event.description}</p>
                      <p className="text-xs text-gray-400 mt-1">📅 {event.start_date}</p>
                    </div>
                    <button onClick={() => copySchema(event.schema, event.id)} className="text-sm bg-gray-100 px-3 py-1 rounded hover:bg-gray-200">
                      {copied === event.id ? '✓ Copied!' : '📋 Copy'}
                    </button>
                  </div>
                ))}
                {events.length === 0 && <div className="p-8 text-center text-gray-500">No events yet. Create one above!</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocalSchema;
